import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const AMBITI = [
  { k: "trattamento", label: "Trattamento Privacy" },
  { k: "nomina",      label: "Nomina (singolo nominato)" },
  { k: "esterno",     label: "Esterno / Fornitore" },
  { k: "azienda",     label: "Azienda" },
  { k: "docente",     label: "Docente" },
];

// Tabelle dichiarabili nel modello (multi-select metadata).
// Le chiavi corrispondono ai nodi del catalogo placeholder esposto da backend.
const TABELLE_OPTS = [
  { k: "azienda",      label: "Azienda",       icon: "🏢" },
  { k: "trattamenti",  label: "Trattamenti",   icon: "📋" },
  { k: "nomine",       label: "Nomine (slot ruoli LR/PT/RE/...)", icon: "✍️" },
  { k: "tabelle",      label: "Sotto-tabelle Trattamento (Tipi/Motivo/Fasi/Rischio/Misure)", icon: "🗂️" },
  { k: "docenti",      label: "Docenti",       icon: "👩‍🏫" },
  { k: "esterni",      label: "Esterni / Fornitori", icon: "🌐" },
  { k: "nomina",       label: "Nomina (dati N_/D_)",  icon: "📜" },
];

// Mapping noti per ambito → descrizione user-friendly del campo sorgente.
// Popolato dinamicamente da GET /privacy/modelli/placeholders-catalog (vedi PrivacyModelliPage)
let KNOWN_MAPPINGS = { trattamento: {}, azienda: {}, docente: {}, nomina: {} };
// Mapping per-tabella (k del catalogo) → { placeholder: descrizione }
let KNOWN_BY_TABELLA = {};
let RUOLO_PREFIX_LABELS = {
  PT: "Titolare", RE: "Responsabile", DP: "DPO", CO: "Controllore", BK: "Backup", SA: "SysAdmin", LR: "Legale Rappresentante",
};

function setKnownMappingsFromCatalog(archivi) {
  // archivi è la struttura tornata da GET .../placeholders-catalog
  // costruisco un dizionario per-ambito-frontend (trattamento/azienda/docente/esterno/nomina)
  const trattamento = {};
  const azienda = {};
  const docente = {};
  const esterno = {};
  const nomina = {};
  // Comune: data stampa
  for (const ph of [{ p: "Data Stampa", desc: "Data corrente (gg/mm/aaaa)" },
                     { p: "DATA STAMPA", desc: "Data corrente (gg/mm/aaaa)" }]) {
    azienda[ph.p] = ph.desc;
    docente[ph.p] = ph.desc;
    esterno[ph.p] = ph.desc;
    trattamento[ph.p] = ph.desc;
    nomina[ph.p] = ph.desc;
  }
  // Mapping per-tabella (chiave = k del catalogo)
  const byTabella = {};
  for (const arch of archivi) {
    const lbl = arch.label;
    byTabella[arch.k] = byTabella[arch.k] || {};
    for (const { p, desc } of arch.placeholders) {
      const fullDesc = `${lbl} → ${desc}`;
      byTabella[arch.k][p] = fullDesc;
      if (arch.k === "azienda") {
        azienda[p] = fullDesc;
        trattamento[p] = fullDesc;
        docente[p] = fullDesc;
        esterno[p] = fullDesc;
        nomina[p] = fullDesc;
      } else if (arch.k === "trattamenti") {
        trattamento[p] = `Trattamento → ${desc}`;
      } else if (arch.k === "nomine") {
        // pattern PT/RE/DP/CO/BK/SA/LR + 1..5 — gestito a parte da classifyPlaceholder
      } else if (arch.k === "tabelle") {
        trattamento[p] = `Tabella → ${desc}`;
      } else if (arch.k === "docenti") {
        docente[p] = `Docente → ${desc}`;
      } else if (arch.k === "esterni") {
        esterno[p] = `Esterno → ${desc}`;
      } else if (arch.k === "nomina") {
        nomina[p] = `Nomina → ${desc}`;
      }
    }
  }
  KNOWN_MAPPINGS = { trattamento, azienda, docente, esterno, nomina };
  KNOWN_BY_TABELLA = byTabella;
}

function classifyPlaceholder(p, ambito, tabelle) {
  // Se sono dichiarate tabelle nel modello, filtra il riconoscimento solo a quelle.
  const norm = p.replace(/\s+/g, " ").trim();
  if (Array.isArray(tabelle) && tabelle.length > 0) {
    for (const t of tabelle) {
      const known = KNOWN_BY_TABELLA[t] || {};
      for (const k of Object.keys(known)) {
        if (k.toLowerCase() === norm.toLowerCase()) {
          return { ok: true, label: known[k] };
        }
      }
      // Pattern ruoli (solo se la tabella "nomine" è inclusa)
      if (t === "nomine") {
        const m = norm.match(/^([A-Z]{2})([1-5])$/);
        if (m && RUOLO_PREFIX_LABELS[m[1]]) {
          return { ok: true, label: `Slot ${RUOLO_PREFIX_LABELS[m[1]]} ${m[2]}/5` };
        }
      }
    }
    return { ok: false, label: `Non incluso nelle tabelle dichiarate (${tabelle.join(", ")})` };
  }
  // Fallback: classificazione per-ambito (comportamento storico)
  const known = KNOWN_MAPPINGS[ambito] || {};
  for (const k of Object.keys(known)) {
    if (k.toLowerCase() === norm.toLowerCase()) {
      return { ok: true, label: known[k] };
    }
  }
  // Pattern ruoli (solo per ambito trattamento)
  if (ambito === "trattamento") {
    const m = norm.match(/^([A-Z]{2})([1-5])$/);
    if (m && RUOLO_PREFIX_LABELS[m[1]]) {
      return { ok: true, label: `Slot ${RUOLO_PREFIX_LABELS[m[1]]} ${m[2]}/5` };
    }
  }
  return { ok: false, label: "Non riconosciuto — sarà ignorato (rimarrà come testo «...»)" };
}

export default function PrivacyModelliPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState(null);
  const [filtroAmbito, setFiltroAmbito] = useState("");
  const [showGuida, setShowGuida] = useState(false);
  const [catalogo, setCatalogo] = useState([]); // archivi[] da backend

  // Carica catalogo placeholder (dinamico da backend) una sola volta
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/privacy/modelli/placeholders-catalog");
        const archivi = r.data?.archivi || [];
        setCatalogo(archivi);
        setKnownMappingsFromCatalog(archivi);
      } catch { /* la guida funzionerà comunque ma senza mapping noti */ }
    })();
  }, []);

  const carica = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/privacy/modelli");
      setItems(r.data?.items || []);
    } catch (e) { setErr(e.response?.data?.detail || "Errore caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { carica(); }, []);

  const salva = async () => {
    try {
      if (editing._isNew) {
        const payload = { ...editing };
        delete payload.codice;
        delete payload._isNew;
        await api.post("/privacy/modelli", payload);
      } else {
        const sezioniArr = Array.isArray(editing.sezioni) ? editing.sezioni : (editing.sezione ? [editing.sezione] : []);
        await api.put(`/privacy/modelli/${encodeURIComponent(editing.codice)}`, {
          nome: editing.nome,
          descrizione: editing.descrizione,
          ambito: editing.ambito,
          sezioni: sezioniArr,
          obbligatorio: !!editing.obbligatorio,
          obbligatorio_se_nominato: !!editing.obbligatorio_se_nominato,
          tipo_nomina_codice: editing.tipo_nomina_codice || "",
          tabelle: editing.tabelle || [],
        });
      }
      setEditing(null);
      await carica();
    } catch (e) { setErr(e.response?.data?.detail || "Errore salvataggio"); }
  };

  const elimina = async (m) => {
    if (!window.confirm(`Eliminare il modello "${m.nome}" (${m.codice})?\nIl file caricato verrà rimosso.`)) return;
    try { await api.delete(`/privacy/modelli/${encodeURIComponent(m.codice)}`); await carica(); }
    catch (e) { setErr(e.response?.data?.detail || "Errore"); }
  };

  const filtered = filtroAmbito ? items.filter(m => m.ambito === filtroAmbito) : items;

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: 18, color: "#37474f" }}>📂 Catalogo Docx</h2>
        <button onClick={() => setShowGuida(true)} data-testid="pm-guida-btn"
          style={{ marginLeft: "auto", background: "white", color: "#0d47a1", border: "1px solid #0d47a1", padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          📖 Guida placeholder
        </button>
        <button onClick={() => setEditing({ _isNew: true, ambito: "trattamento", nome: "", descrizione: "", tabelle: [] })}
          data-testid="pm-new-btn"
          style={{ background: "#2e7d32", color: "white", border: "none", padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          + Nuovo modello
        </button>
      </div>
      <div style={{ fontSize: 11, color: "#555", marginBottom: 12, padding: "6px 10px", background: "#e3f2fd", border: "1px solid #bbdefb", borderRadius: 4 }}>
        <b>📘 Come funziona</b> · <b>Ambito</b>: pagina in cui il modello appare nel dropdown <i>Stampa/Anteprima</i> (Trattamenti / Nomine / Esterni / Azienda / Docente).
        <b> Tabelle interessate</b>: archivi da cui il modello preleva i placeholder utilizzabili nel <code>.docx</code>.
        <b> Placeholders</b>: <span style={{ color: "#0d47a1" }}>🎯 disponibili</span> (dalle tabelle) /
        <span style={{ color: "#1b5e20" }}>✓ usati nel docx</span> /
        <span style={{ color: "#e65100" }}>⚠ orfani</span> (nel docx ma fuori dalle tabelle → non si compilano).
      </div>

      {/* Filtro ambiti: pill orizzontali */}
      <div data-testid="pm-filtro-ambito-bar"
        style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12, padding: "6px 8px", background: "#fafafa", border: "1px solid #e0e0e0", borderRadius: 4 }}>
        <span style={{ fontSize: 11, color: "#666", fontWeight: 700, alignSelf: "center", marginRight: 4 }}>FILTRO AMBITO:</span>
        {[{ k: "", label: "Tutti", count: items.length }, ...AMBITI.map(a => ({ ...a, count: items.filter(m => m.ambito === a.k).length }))].map(opt => {
          const active = filtroAmbito === opt.k;
          return (
            <button key={opt.k || "all"}
              onClick={() => setFiltroAmbito(opt.k)}
              data-testid={`pm-filtro-pill-${opt.k || "all"}`}
              style={{
                padding: "5px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 16,
                background: active ? "#0d47a1" : "white",
                color: active ? "white" : "#37474f",
                border: active ? "1px solid #0d47a1" : "1px solid #cfd8dc",
              }}>
              {opt.label}
              <span style={{ marginLeft: 6, padding: "1px 6px", background: active ? "rgba(255,255,255,0.25)" : "#eceff1", color: active ? "white" : "#666", fontSize: 10, borderRadius: 8 }}>{opt.count}</span>
            </button>
          );
        })}
      </div>

      {err && <div style={{ padding: 10, background: "#fff0f0", color: "#c00", fontSize: 12, marginBottom: 10 }} data-testid="pm-error">{err}</div>}

      <div style={{ background: "white", border: "1px solid #ccc", overflow: "auto" }}>
        <table className="sw-table sw-table-compact" style={{ width: "100%", fontSize: 11, tableLayout: "auto" }}>
          <thead className="thead-blue">
            <tr>
              <th style={{ width: 60 }}>COD</th>
              <th style={{ minWidth: 220 }}>NOME DOCUMENTO</th>
              <th style={{ minWidth: 320 }}>FILE DOCX</th>
              <th style={{ minWidth: 130 }}>FASCICOLO/I</th>
              <th style={{ width: 170, textAlign: "center" }}>AZIONI</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} style={{ padding: 20, textAlign: "center", color: "#888" }}>Caricamento...</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: "center", color: "#888" }}>
                Nessun modello presente. Crea il primo con il pulsante <b>+ Nuovo modello</b>.
              </td></tr>
            )}
            {filtered.map((m, i) => (
              <ModelloRow key={m.codice} m={m} i={i} catalogo={catalogo} onEdit={() => setEditing({ ...m })} onDelete={() => elimina(m)} onReload={carica} />
            ))}
          </tbody>
        </table>
      </div>

      {editing && <EditModal editing={editing} setEditing={setEditing} onSave={salva} />}
      {showGuida && <GuidaModal archivi={catalogo} onClose={() => setShowGuida(false)} />}
    </div>
  );
}

// ============================================================
// GUIDA — placeholder organizzati per ARCHIVIO
// ============================================================


function GuidaModal({ archivi, onClose }) {
  const [archivioSel, setArchivioSel] = useState(archivi[0]?.k || "");
  const [filtro, setFiltro] = useState("");
  const [copiato, setCopiato] = useState("");

  const copia = async (p) => {
    const txt = `«${p}»`;
    try {
      await navigator.clipboard.writeText(txt);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = txt;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* ignore */ }
      ta.remove();
    }
    setCopiato(p);
    setTimeout(() => setCopiato(c => (c === p ? "" : c)), 1500);
  };

  // Filtro: se c'è testo, conto le occorrenze per archivio
  const f = filtro.trim().toLowerCase();
  const matchInArch = (a) =>
    !f ? a.placeholders : a.placeholders.filter(x => x.p.toLowerCase().includes(f) || x.desc.toLowerCase().includes(f));

  const arch = archivi.find(a => a.k === archivioSel) || archivi[0];
  if (!arch) {
    return (
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div onClick={e => e.stopPropagation()} style={{ background: "white", padding: 30, fontSize: 13, color: "#666" }}>
          Caricamento catalogo placeholder...
        </div>
      </div>
    );
  }
  const visibili = matchInArch(arch);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} data-testid="pm-guida-modal"
        style={{ background: "white", width: 1100, maxHeight: "92vh", display: "flex", flexDirection: "column", padding: 0 }}>
        <div style={{ padding: "14px 20px", background: "#0d47a1", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>📖 Guida placeholder — divisi per archivio</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>

        <div style={{ padding: "10px 20px", background: "#e3f2fd", borderBottom: "1px solid #bbdefb", fontSize: 12, color: "#0d47a1" }}>
          <b>Sintassi:</b> nel docx scrivi <Ph>«NomeCampo»</Ph> (testo normale) oppure usa <i>Inserisci → Parti rapide → MergeField</i>. Per <code>«</code> e <code>»</code> in Word: <b>Alt+0171</b> e <b>Alt+0187</b>. Click su <b>📋 Copia</b> per copiare il placeholder esatto.
        </div>

        <div style={{ padding: "8px 20px", borderBottom: "1px solid #eee", background: "#fafafa", display: "flex", gap: 10, alignItems: "center" }}>
          <input type="text" placeholder="🔍 Filtra in tutti gli archivi (nome o descrizione)..."
            value={filtro} onChange={e => setFiltro(e.target.value)}
            data-testid="pm-guida-filter"
            style={{ flex: 1, padding: "6px 10px", border: "1px solid #ccc", fontSize: 12 }} />
        </div>

        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* SIDEBAR ARCHIVI */}
          <div style={{ width: 240, background: "#37474f", color: "white", overflowY: "auto" }}>
            <div style={{ padding: "8px 14px", fontSize: 10, fontWeight: 700, opacity: 0.7, textTransform: "uppercase", borderBottom: "1px solid #455a64" }}>Archivi</div>
            {archivi.map(a => {
              const cnt = matchInArch(a).length;
              const hidden = f && cnt === 0;
              const sel = a.k === archivioSel;
              return (
                <div key={a.k} onClick={() => setArchivioSel(a.k)} data-testid={`pm-guida-arch-${a.k}`}
                  style={{
                    padding: "10px 14px", cursor: "pointer", fontSize: 12, borderBottom: "1px solid #455a64",
                    background: sel ? "#29b6f6" : (hidden ? "#263238" : "#37474f"),
                    color: hidden ? "#78909c" : "white",
                    opacity: hidden ? 0.55 : 1,
                  }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 16 }}>{a.icon}</span>
                    <span style={{ fontWeight: 700, flex: 1 }}>{a.label}</span>
                    <span style={{
                      padding: "1px 6px", fontSize: 10, fontWeight: 700,
                      background: a.state === "futuro" ? "#ff9800" : (sel ? "white" : "#1976d2"),
                      color: a.state === "futuro" ? "white" : (sel ? "#0d47a1" : "white"),
                      borderRadius: 2,
                    }}>{cnt}</span>
                  </div>
                  {a.state === "futuro" && (
                    <div style={{ fontSize: 9, opacity: 0.85, marginTop: 2, fontStyle: "italic" }}>in arrivo</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* CONTENT */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "10px 16px", borderBottom: "1px solid #eee", background: "white" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0d47a1", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 18 }}>{arch.icon}</span>
                <span>{arch.label}</span>
                {arch.state === "futuro" && <span style={{ padding: "2px 8px", fontSize: 10, fontWeight: 700, background: "#fff3e0", color: "#e65100", border: "1px solid #ffcc80" }}>📅 IN ARRIVO</span>}
                <span style={{ fontSize: 11, color: "#666", fontWeight: 400, marginLeft: 8 }}>· {visibili.length}/{arch.placeholders.length} placeholder{f ? " (filtrati)" : ""}</span>
              </div>
              <div style={{ fontSize: 11, color: "#555", marginTop: 4, lineHeight: 1.4 }}>{arch.desc}</div>
            </div>

            <div style={{ flex: 1, overflow: "auto" }}>
              {arch.placeholders.length === 0 ? (
                <div style={{ padding: 30, textAlign: "center", color: "#888", fontSize: 12 }}>
                  Nessun placeholder definito per questo archivio (in arrivo).
                </div>
              ) : (
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
                  <thead className="thead-blue" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ padding: "8px 12px", textAlign: "left", width: 220 }}>PLACEHOLDER</th>
                      <th style={{ padding: "8px 12px", textAlign: "left" }}>COSA STAMPA</th>
                      <th style={{ padding: "8px 12px", textAlign: "center", width: 120 }}>COPIA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibili.length === 0 && (
                      <tr><td colSpan={3} style={{ padding: 30, textAlign: "center", color: "#888" }}>Nessun placeholder corrisponde al filtro in questo archivio.</td></tr>
                    )}
                    {visibili.map(({ p, desc }) => (
                      <tr key={p} data-testid={`pm-guida-row-${arch.k}-${p}`} style={{ borderBottom: "1px solid #eee", background: arch.state === "futuro" ? "#fff8e1" : "white" }}>
                        <td style={{ padding: "5px 12px" }}>
                          <code style={{ display: "inline-block", padding: "2px 8px", background: "#fff3e0", color: "#e65100", border: "1px solid #ffcc80", fontFamily: "Consolas, monospace", fontSize: 12, fontWeight: 700 }}>«{p}»</code>
                        </td>
                        <td style={{ padding: "5px 12px", color: "#37474f" }}>{desc}</td>
                        <td style={{ padding: "5px 12px", textAlign: "center" }}>
                          <button onClick={() => copia(p)} data-testid={`pm-guida-copy-${arch.k}-${p}`}
                            title={`Copia «${p}» negli appunti`}
                            disabled={arch.state === "futuro"}
                            style={{
                              padding: "4px 12px", border: "none", fontSize: 11, fontWeight: 700,
                              cursor: arch.state === "futuro" ? "not-allowed" : "pointer",
                              background: arch.state === "futuro" ? "#bdbdbd" : (copiato === p ? "#2e7d32" : "#1565c0"),
                              color: "white", minWidth: 90, opacity: arch.state === "futuro" ? 0.7 : 1,
                            }}>
                            {arch.state === "futuro" ? "—" : (copiato === p ? "✓ Copiato!" : "📋 Copia")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Box istruzioni sotto-tabelle solo quando archivio "tabelle" */}
            {arch.k === "tabelle" && (
              <div style={{ padding: "10px 16px", background: "#fff8e1", borderTop: "1px solid #ffe082", fontSize: 11, color: "#5d4037" }}>
                <b>📌 Come si usano:</b> in Word crea una tabella di 2 colonne con UNA sola riga di dati. Nella prima cella metti il trigger (es. <Ph>«Fasi»</Ph>), nella seconda <Ph>«TDescription»</Ph>. Il sistema duplica la riga per ogni voce collegata.
                <div style={{ background: "white", border: "1px dashed #bcaaa4", padding: 8, margin: "6px 0 0", fontFamily: "Consolas, monospace", fontSize: 11 }}>
                  ┌────────┬────────────────┐<br/>
                  │ «Fasi» │ «TDescription» │  ← UNA riga modello<br/>
                  └────────┴────────────────┘
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Ph = ({ children }) => (
  <code style={{ display: "inline-block", padding: "1px 6px", background: "#fff3e0", color: "#e65100", border: "1px solid #ffcc80", fontFamily: "Consolas, monospace", fontSize: 11 }}>{children}</code>
);

function InlineNomeEditor({ modello, onSaved, testid }) {
  const [val, setVal] = useState(modello.nome || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { setVal(modello.nome || ""); }, [modello.nome]);
  const commit = async () => {
    const v = (val || "").trim();
    if (v === (modello.nome || "")) return;
    if (!v) { setVal(modello.nome || ""); return; }
    setSaving(true); setErr(null);
    try {
      await api.put(`/privacy/modelli/${encodeURIComponent(modello.codice)}`, { nome: v });
      if (onSaved) await onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore salvataggio");
      setVal(modello.nome || "");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div>
      <input value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
          if (e.key === "Escape") { setVal(modello.nome || ""); e.currentTarget.blur(); }
        }}
        disabled={saving}
        data-testid={testid}
        title="Clicca per modificare. Premi Invio per salvare, Esc per annullare."
        style={{
          width: "100%", border: "1px solid transparent", padding: "4px 6px",
          fontWeight: 600, fontSize: 12, background: saving ? "#fff8e1" : "transparent",
          fontFamily: "inherit", borderRadius: 2,
        }}
        onFocus={e => { e.target.style.border = "1px solid #1565c0"; e.target.style.background = "white"; }}
        onMouseEnter={e => { if (document.activeElement !== e.target) e.target.style.border = "1px dashed #cfd8dc"; }}
        onMouseLeave={e => { if (document.activeElement !== e.target) e.target.style.border = "1px solid transparent"; }}
      />
      {modello.descrizione && (
        <div style={{ fontSize: 10, color: "#888", fontWeight: 400, marginTop: 1, padding: "0 6px" }}>{modello.descrizione}</div>
      )}
      {err && <div style={{ fontSize: 10, color: "#c62828", marginTop: 2, padding: "0 6px" }}>⚠ {err}</div>}
    </div>
  );
}

function ToggleNuovo({ modello, onSaved, testid }) {
  const [val, setVal] = useState(!!modello.nuovo);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(!!modello.nuovo); }, [modello.nuovo]);
  const toggle = async () => {
    if (saving) return;
    const next = !val;
    setVal(next); setSaving(true);
    try {
      await api.put(`/privacy/modelli/${encodeURIComponent(modello.codice)}`, { nuovo: next });
      if (onSaved) await onSaved();
    } catch (e) {
      setVal(!next);
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };
  return (
    <button onClick={toggle} disabled={saving} data-testid={testid}
      title={val ? "Documento marcato come NUOVO (apparirà evidenziato nel fascicolo). Clicca per rimuovere." : "Clicca per marcare come NUOVO (verrà evidenziato nel fascicolo del docente e nel link pubblico)."}
      style={{ flexShrink: 0, padding: val ? "2px 8px" : "2px 6px", background: val ? "#ef6c00" : "white", color: val ? "white" : "#bbb", border: val ? "1px solid #ef6c00" : "1px dashed #ddd", borderRadius: 10, fontSize: 9, fontWeight: 800, cursor: saving ? "wait" : "pointer", letterSpacing: 0.3, whiteSpace: "nowrap" }}>
      {val ? "🆕 NUOVO" : "🆕"}
    </button>
  );
}


function ModelloRow({ m, i, catalogo, onEdit, onDelete, onReload }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/privacy/modelli/${encodeURIComponent(m.codice)}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await onReload();
    } catch (err) { alert(err.response?.data?.detail || "Errore upload"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const downloadOriginale = () => {
    window.open(`${api.defaults.baseURL}/privacy/modelli/${encodeURIComponent(m.codice)}/file`, "_blank");
  };
  const placeholders = m.placeholders || [];
  const classified = placeholders.map(p => ({ name: p, ...classifyPlaceholder(p, m.ambito, m.tabelle) }));
  const knownCount = classified.filter(c => c.ok).length;
  const unknownCount = classified.length - knownCount;
  // Conta i trigger ATTESI (legati alle tabelle del modello) ma MANCANTI nel docx
  const missingTriggers = (() => {
    if (!Array.isArray(catalogo) || catalogo.length === 0) return [];
    const usedKeys = Array.isArray(m.tabelle) ? m.tabelle : [];
    if (usedKeys.length === 0) return [];
    const docxSet = new Set((m.placeholders || []).map(p => p.toLowerCase()));
    const out = [];
    for (const arch of catalogo) {
      if (!usedKeys.includes(arch.k)) continue;
      for (const ph of arch.placeholders || []) {
        if (typeof ph.desc === "string" && ph.desc.startsWith("[Trigger]") && !docxSet.has(ph.p.toLowerCase())) {
          out.push(ph.p);
        }
      }
    }
    return out;
  })();
  // Calcola i placeholder DISPONIBILI in base alle tabelle/ambito (totale teorico).
  const dispCount = (() => {
    if (!Array.isArray(catalogo) || catalogo.length === 0) return 0;
    const usedKeys = (m.tabelle && m.tabelle.length > 0)
      ? m.tabelle
      : (() => {
        const map = {
          trattamento: ["azienda", "trattamenti", "tabelle", "nomine"],
          esterno:     ["azienda", "esterni"],
          nomina:      ["azienda", "nomina", "nomine"],
          azienda:     ["azienda"],
          docente:     ["azienda", "docenti"],
        };
        return map[m.ambito] || ["azienda"];
      })();
    return catalogo.filter(a => usedKeys.includes(a.k)).reduce((s, a) => s + (a.placeholders?.length || 0), 0);
  })();
  // Etichetta settore (ambito) per badge
  const SETTORE_INFO = {
    trattamento: { label: "Trattamento", color: "#1565c0", bg: "#e3f2fd", icon: "📋" },
    nomina:      { label: "Nomina",      color: "#7b1fa2", bg: "#f3e5f5", icon: "✍️" },
    esterno:     { label: "Esterno",     color: "#00838f", bg: "#e0f7fa", icon: "🌐" },
    azienda:     { label: "Azienda",     color: "#37474f", bg: "#eceff1", icon: "🏢" },
    docente:     { label: "Docente",     color: "#558b2f", bg: "#f1f8e9", icon: "👩‍🏫" },
  };
  const sett = SETTORE_INFO[m.ambito] || { label: m.ambito || "?", color: "#666", bg: "#fafafa", icon: "❓" };

  // Etichetta sezione/fascicolo (3 fascicoli possibili: trattamento, anagrafica, esterno)
  const SEZIONI_LABELS = {
    trattamento: "Trattamento",
    anagrafica: "Anagrafica",
    esterno: "Esterni",
  };

  // Tabelle del modello: icone
  const TAB_ICONS = {
    azienda: "🏢", trattamenti: "📋", nomine: "✍️", tabelle: "🗂️",
    docenti: "👩‍🏫", esterni: "🌐", nomina: "📜",
  };

  return (
    <>
    <tr data-testid={`pm-row-${i}`}>
      <td style={{ fontFamily: "Consolas, monospace", fontWeight: 700, fontSize: 12 }}>{m.codice}</td>
      <td>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <ToggleNuovo modello={m} onSaved={onReload} testid={`pm-row-nuovo-${i}`} />
          <div style={{ flex: 1 }}>
            <InlineNomeEditor
              modello={m}
              onSaved={onReload}
              testid={`pm-row-nome-${i}`} />
          </div>
        </div>
      </td>
      <td style={{ fontSize: 10, fontFamily: "Consolas, monospace", color: m.filename ? "#1b5e20" : "#999", wordBreak: "break-word", maxWidth: 360 }}>
        {m.filename ? <>📄 {m.filename}</> : "(nessun file)"}
        {m.size && <span style={{ marginLeft: 4, color: "#888", fontSize: 9 }}>{Math.round(m.size / 1024)}KB</span>}
      </td>
      <td style={{ fontSize: 10 }}>
        {(() => {
          const sezArr = Array.isArray(m.sezioni) && m.sezioni.length > 0
            ? m.sezioni
            : (m.sezione ? [m.sezione] : []);
          if (sezArr.length === 0) {
            return <span style={{ fontSize: 10, color: "#bbb", fontStyle: "italic" }}>— (stampa singola)</span>;
          }
          const COLOR = { trattamento: "#1565c0", anagrafica: "#6a1b9a", esterno: "#00838f" };
          return (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              {sezArr.map(sk => (
                <span key={sk} data-testid={`pm-row-sezione-${i}-${sk}`}
                  style={{ display: "inline-block", padding: "3px 8px", background: `${COLOR[sk] || "#7b1fa2"}15`, color: COLOR[sk] || "#7b1fa2", border: `1px solid ${COLOR[sk] || "#7b1fa2"}80`, fontSize: 10, fontWeight: 700, borderRadius: 10 }}>
                  📁 {SEZIONI_LABELS[sk] || sk}
                </span>
              ))}
            </div>
          );
        })()}
        {(m.obbligatorio || m.obbligatorio_se_nominato || m.tipo_nomina_codice) && (
          <div style={{ marginTop: 3, display: "flex", gap: 3, flexWrap: "wrap" }}>
            {m.obbligatorio && (
              <span title="Sempre obbligatorio"
                style={{ padding: "1px 5px", background: "#c62828", color: "white", fontSize: 9, fontWeight: 700, borderRadius: 2 }}>📌 OBB</span>
            )}
            {m.obbligatorio_se_nominato && (
              <span title="Obbligatorio se nominato"
                style={{ padding: "1px 5px", background: "#ef6c00", color: "white", fontSize: 9, fontWeight: 700, borderRadius: 2 }}>🎯 SE NOM</span>
            )}
            {m.tipo_nomina_codice && (
              <span title={`Specifico per tipo nomina ${m.tipo_nomina_codice}`}
                style={{ padding: "1px 5px", background: "#7b1fa2", color: "white", fontSize: 9, fontWeight: 700, borderRadius: 2 }}>📜 {m.tipo_nomina_codice}</span>
            )}
          </div>
        )}
      </td>
      <td style={{ textAlign: "center", whiteSpace: "nowrap", padding: 3 }}>
        <button onClick={onEdit} data-testid={`pm-edit-${i}`} title="Modifica"
          style={{ padding: "3px 7px", background: "white", color: "#0d47a1", border: "1px solid #0d47a1", fontSize: 11, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>
          ✏
        </button>
        <label data-testid={`pm-upload-${i}`}
          title={m.filename ? "Sostituisci file .docx" : "Carica .docx"}
          style={{ display: "inline-block", padding: "3px 7px", background: "#1565c0", color: "white", fontSize: 11, fontWeight: 700, cursor: busy ? "wait" : "pointer", marginRight: 3, borderRadius: 2 }}>
          {busy ? "…" : (m.filename ? "↻" : "📥")}
          <input ref={fileRef} type="file" accept=".docx" onChange={upload} disabled={busy} style={{ display: "none" }} />
        </label>
        {m.filename && (
          <button onClick={downloadOriginale} data-testid={`pm-download-${i}`} title="Scarica originale"
            style={{ padding: "3px 7px", background: "white", color: "#37474f", border: "1px solid #37474f", fontSize: 11, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>
            ⬇
          </button>
        )}
        <button onClick={onDelete} data-testid={`pm-delete-${i}`} title="Elimina"
          style={{ padding: "3px 7px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
          🗑
        </button>
      </td>
    </tr>
    {showAnalysis && (
      <PlaceholderAnalysisModal
        modello={m}
        classified={classified}
        knownCount={knownCount}
        unknownCount={unknownCount}
        catalogo={catalogo || []}
        onClose={() => setShowAnalysis(false)}
      />
    )}
    </>
  );
}

// ============================================================
// PLACEHOLDER ANALYSIS MODAL — Popup con i placeholder rilevati
// nel singolo template. Ogni riga ha bottone Copia.
// ============================================================
function PlaceholderAnalysisModal({ modello, classified, knownCount, unknownCount, catalogo, onClose }) {
  const [copiato, setCopiato] = useState("");
  const [filtro, setFiltro] = useState("");
  const [tab, setTab] = useState("documento"); // documento | potenziali

  const copia = async (p) => {
    const txt = `«${p}»`;
    try {
      await navigator.clipboard.writeText(txt);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = txt;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* ignore */ }
      ta.remove();
    }
    setCopiato(p);
    setTimeout(() => setCopiato(c => (c === p ? "" : c)), 1500);
  };

  const f = filtro.trim().toLowerCase();
  const visibili = !f ? classified : classified.filter(c => c.name.toLowerCase().includes(f) || c.label.toLowerCase().includes(f));

  // Calcola placeholder potenziali in base alle tabelle del modello (o agli archivi default per ambito).
  const archiviPotenziali = (() => {
    if (!Array.isArray(catalogo) || catalogo.length === 0) return [];
    const usedKeys = (modello.tabelle && modello.tabelle.length > 0)
      ? modello.tabelle
      // Fallback per ambito: sempre azienda + l'archivio specifico
      : (() => {
        const map = {
          trattamento: ["azienda", "trattamenti", "tabelle", "nomine"],
          esterno:     ["azienda", "esterni"],
          nomina:      ["azienda", "nomina"],
          azienda:     ["azienda"],
          docente:     ["azienda", "docenti"],
        };
        return map[modello.ambito] || ["azienda"];
      })();
    return catalogo.filter(a => usedKeys.includes(a.k));
  })();
  const placeholderUsatiSet = new Set((classified || []).map(c => c.name.toLowerCase()));
  const archiviPotenzialiFiltrati = archiviPotenziali.map(arch => {
    const phs = arch.placeholders.filter(ph => !f || ph.p.toLowerCase().includes(f) || ph.desc.toLowerCase().includes(f));
    return { ...arch, placeholders: phs };
  }).filter(a => a.placeholders.length > 0);
  const totalPotenziali = archiviPotenziali.reduce((s, a) => s + a.placeholders.length, 0);

  // Identifica i TRIGGER attesi (placeholder con descrizione che inizia per "[Trigger]")
  // legati alle tabelle dichiarate dal modello. Se uno di questi trigger NON è presente nel
  // .docx caricato, mostriamo un avviso rosso ben visibile in cima al modale.
  const triggersAttesi = (() => {
    if (!Array.isArray(catalogo) || catalogo.length === 0) return [];
    const usedKeys = Array.isArray(modello.tabelle) ? modello.tabelle : [];
    if (usedKeys.length === 0) return [];
    const triggers = [];
    for (const arch of catalogo) {
      if (!usedKeys.includes(arch.k)) continue;
      for (const ph of arch.placeholders || []) {
        if (typeof ph.desc === "string" && ph.desc.startsWith("[Trigger]")) {
          triggers.push({ p: ph.p, archivio: arch.label, archivioK: arch.k });
        }
      }
    }
    return triggers;
  })();
  const triggersMancanti = triggersAttesi.filter(t => !placeholderUsatiSet.has(t.p.toLowerCase()));
  // Se la tabella «tabelle» è collegata, basta che ALMENO UNO dei suoi 6 trigger sia presente
  // (Luoghi/Tipi/Motivo/Fasi/Rischio/Misure). In quel caso non segnaliamo "tutti mancanti": lasciamo
  // segnalare solo quelli specifici che mancano (utile come check granulare).
  // Se invece la tabella è «nomine», l'unico trigger «Nomine» è obbligatorio.

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} data-testid={`pm-analysis-modal-${modello.codice}`}
        style={{ background: "white", width: 920, maxHeight: "90vh", display: "flex", flexDirection: "column", padding: 0 }}>
        <div style={{ padding: "14px 20px", background: "#0d47a1", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>🔍 Placeholder — {modello.codice} · {modello.nome}</div>
            <div style={{ fontSize: 11, opacity: 0.9, marginTop: 2 }}>
              <span style={{ color: "#90caf9", fontWeight: 700 }}>🎯 {totalPotenziali} disponibili</span>
              {" · "}
              <span style={{ color: "#a5d6a7", fontWeight: 700 }}>✓ {knownCount} usati nel docx</span>
              {unknownCount > 0 && <> {" · "}<span style={{ color: "#ffcc80", fontWeight: 700 }}>⚠ {unknownCount} orfani</span></>}
              {modello.tabelle?.length > 0 && (
                <span style={{ marginLeft: 12, opacity: 0.9 }}>· Tabelle: <b>{modello.tabelle.join(", ")}</b></span>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>

        {/* Banner avviso: trigger sotto-tabella dichiarati nel modello ma MANCANTI nel docx */}
        {triggersMancanti.length > 0 && (
          <div data-testid="pm-an-missing-triggers"
            style={{ padding: "12px 20px", background: "#ffebee", borderBottom: "2px solid #ef5350", color: "#b71c1c", fontSize: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 13 }}>
              ⚠ Trigger mancanti nel documento Word
            </div>
            <div style={{ marginBottom: 6, color: "#4e342e" }}>
              Le tabelle collegate a questo modello richiedono i trigger qui sotto, ma il <code>.docx</code> caricato non li contiene.
              Senza il trigger il sistema <b>non duplica le righe</b> della sotto-tabella e l&apos;elenco resterà vuoto.
            </div>
            <ul style={{ margin: "4px 0 4px 20px", padding: 0 }}>
              {triggersMancanti.map(t => (
                <li key={t.p} style={{ marginBottom: 3 }}>
                  <code style={{ padding: "1px 6px", background: "#fff3e0", color: "#e65100", border: "1px solid #ffcc80", fontFamily: "Consolas, monospace", fontWeight: 700 }}>«{t.p}»</code>
                  <span style={{ marginLeft: 8, color: "#5d4037" }}>— sorgente: <b>{t.archivio}</b></span>
                </li>
              ))}
            </ul>
            <div style={{ marginTop: 8, padding: "8px 10px", background: "#fff8e1", border: "1px dashed #ffb300", color: "#5d4037", fontSize: 11, lineHeight: 1.5 }}>
              📌 <b>Cosa fare:</b> apri il file in Word, individua la tabella in cui vuoi l&apos;elenco dinamico
              e nella <b>prima cella della riga dati</b> scrivi esattamente il trigger
              (es. <code style={{ padding: "1px 4px", background: "#fff3e0", border: "1px solid #ffcc80", fontFamily: "Consolas, monospace" }}>«Nomine»</code>).
              Salva il documento, poi clicca <b>↻ Sost. file</b> per ricaricarlo.
            </div>
          </div>
        )}

        {/* Tab switcher */}
        <div style={{ display: "flex", borderBottom: "1px solid #eee", background: "#fafafa" }}>
          <button onClick={() => setTab("documento")} data-testid="pm-an-tab-doc"
            style={{
              flex: 1, padding: "10px 16px", border: "none", background: tab === "documento" ? "white" : "transparent",
              borderBottom: tab === "documento" ? "3px solid #0d47a1" : "3px solid transparent",
              color: tab === "documento" ? "#0d47a1" : "#666", fontSize: 12, fontWeight: 700, cursor: "pointer",
            }}>
            📄 Nel documento <span style={{ marginLeft: 4, padding: "1px 6px", background: "#eceff1", borderRadius: 8, fontSize: 10 }}>{classified.length}</span>
          </button>
          <button onClick={() => setTab("potenziali")} data-testid="pm-an-tab-pot"
            style={{
              flex: 1, padding: "10px 16px", border: "none", background: tab === "potenziali" ? "white" : "transparent",
              borderBottom: tab === "potenziali" ? "3px solid #0d47a1" : "3px solid transparent",
              color: tab === "potenziali" ? "#0d47a1" : "#666", fontSize: 12, fontWeight: 700, cursor: "pointer",
            }}>
            ✨ Potenziali (dalle tabelle)
            <span style={{ marginLeft: 4, padding: "1px 6px", background: "#eceff1", borderRadius: 8, fontSize: 10 }}>{totalPotenziali}</span>
          </button>
        </div>

        <div style={{ padding: "8px 20px", borderBottom: "1px solid #eee", display: "flex", gap: 10, alignItems: "center", background: "#fafafa" }}>
          <input type="text" placeholder="🔍 Filtra..."
            value={filtro} onChange={e => setFiltro(e.target.value)}
            data-testid="pm-analysis-filter"
            style={{ flex: 1, padding: "6px 10px", border: "1px solid #ccc", fontSize: 12 }} />
          <span style={{ fontSize: 11, color: "#666" }}>
            {tab === "documento" ? `${visibili.length} / ${classified.length}` : `${archiviPotenzialiFiltrati.reduce((s, a) => s + a.placeholders.length, 0)} / ${totalPotenziali}`}
          </span>
        </div>

        <div style={{ flex: 1, overflow: "auto" }}>
          {tab === "documento" && (
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
              <thead className="thead-blue" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <tr>
                  <th style={{ padding: "8px 12px", textAlign: "left", width: 220 }}>PLACEHOLDER</th>
                  <th style={{ padding: "8px 12px", textAlign: "center", width: 80 }}>STATO</th>
                  <th style={{ padding: "8px 12px", textAlign: "left" }}>MAPPING / NOTE</th>
                  <th style={{ padding: "8px 12px", textAlign: "center", width: 110 }}>COPIA</th>
                </tr>
              </thead>
              <tbody>
                {visibili.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: 30, textAlign: "center", color: "#888" }}>Nessun placeholder corrisponde al filtro.</td></tr>
                )}
                {visibili.map(c => (
                  <tr key={c.name} data-testid={`pm-an-row-${c.name}`} style={{ borderBottom: "1px solid #eee", background: c.ok ? "white" : "#fff8e1" }}>
                    <td style={{ padding: "5px 12px" }}>
                      <code style={{ display: "inline-block", padding: "2px 8px", background: "#fff3e0", color: "#e65100", border: "1px solid #ffcc80", fontFamily: "Consolas, monospace", fontSize: 12, fontWeight: 700 }}>«{c.name}»</code>
                    </td>
                    <td style={{ padding: "5px 12px", textAlign: "center" }}>
                      {c.ok
                        ? <span style={{ padding: "2px 8px", background: "#e8f5e9", color: "#1b5e20", border: "1px solid #a5d6a7", fontWeight: 700, fontSize: 10 }}>✓ MAPPATO</span>
                        : <span style={{ padding: "2px 8px", background: "#fff3e0", color: "#e65100", border: "1px solid #ffcc80", fontWeight: 700, fontSize: 10 }}>⚠ IGNORATO</span>}
                    </td>
                    <td style={{ padding: "5px 12px", color: "#37474f" }}>{c.label}</td>
                    <td style={{ padding: "5px 12px", textAlign: "center" }}>
                      <button onClick={() => copia(c.name)} data-testid={`pm-an-copy-${c.name}`}
                        title={`Copia «${c.name}» negli appunti`}
                        style={{ padding: "4px 12px", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", background: copiato === c.name ? "#2e7d32" : "#1565c0", color: "white", minWidth: 90 }}>
                        {copiato === c.name ? "✓ Copiato!" : "📋 Copia"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {tab === "potenziali" && (
            <div style={{ padding: 0 }}>
              {archiviPotenziali.length === 0 && (
                <div style={{ padding: 30, textAlign: "center", color: "#888", fontSize: 12 }}>
                  Nessuna tabella collegata al modello. Apri il modello in modifica e seleziona almeno una <b>Tabella interessata</b>.
                </div>
              )}
              {archiviPotenzialiFiltrati.map(arch => (
                <div key={arch.k} data-testid={`pm-an-pot-${arch.k}`}>
                  <div style={{ position: "sticky", top: 0, background: "#37474f", color: "white", padding: "6px 12px", fontSize: 12, fontWeight: 700, zIndex: 1 }}>
                    {arch.icon} {arch.label}
                    <span style={{ marginLeft: 8, fontSize: 10, opacity: 0.85 }}>({arch.placeholders.length})</span>
                  </div>
                  <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
                    <tbody>
                      {arch.placeholders.map(ph => {
                        const usato = placeholderUsatiSet.has(ph.p.toLowerCase());
                        return (
                          <tr key={ph.p} data-testid={`pm-an-pot-row-${ph.p}`} style={{ borderBottom: "1px solid #eee", background: usato ? "#e8f5e9" : "white" }}>
                            <td style={{ padding: "5px 12px", width: 220 }}>
                              <code style={{ display: "inline-block", padding: "2px 8px", background: "#fff3e0", color: "#e65100", border: "1px solid #ffcc80", fontFamily: "Consolas, monospace", fontSize: 12, fontWeight: 700 }}>«{ph.p}»</code>
                            </td>
                            <td style={{ padding: "5px 12px", color: "#555" }}>{ph.desc}</td>
                            <td style={{ padding: "5px 12px", textAlign: "center", width: 110 }}>
                              {usato
                                ? <span style={{ padding: "2px 8px", background: "#e8f5e9", color: "#1b5e20", border: "1px solid #a5d6a7", fontWeight: 700, fontSize: 10 }}>✓ usato</span>
                                : <span style={{ padding: "2px 8px", background: "#fafafa", color: "#888", border: "1px solid #cfd8dc", fontWeight: 700, fontSize: 10 }}>— non usato</span>}
                            </td>
                            <td style={{ padding: "5px 12px", textAlign: "center", width: 110 }}>
                              <button onClick={() => copia(ph.p)} data-testid={`pm-an-pot-copy-${ph.p}`}
                                title={`Copia «${ph.p}» negli appunti`}
                                style={{ padding: "4px 12px", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", background: copiato === ph.p ? "#2e7d32" : "#1565c0", color: "white", minWidth: 90 }}>
                                {copiato === ph.p ? "✓ Copiato!" : "📋 Copia"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
              {archiviPotenziali.length > 0 && archiviPotenzialiFiltrati.length === 0 && (
                <div style={{ padding: 30, textAlign: "center", color: "#888", fontSize: 12 }}>Nessun placeholder potenziale corrisponde al filtro.</div>
              )}
            </div>
          )}
        </div>

        {tab === "documento" && unknownCount > 0 && (
          <div style={{ padding: "10px 20px", background: "#fff8e1", borderTop: "1px solid #ffe082", fontSize: 11, color: "#5d4037" }}>
            <b>⚠ Placeholder orfani</b> → presenti nel docx ma non collegati a nessuna delle <b>tabelle interessate</b> di questo modello. Resteranno come testo «...» nel documento generato. Aggiungi la tabella mancante in modifica modello, oppure correggi l&apos;ortografia del placeholder nel template.
          </div>
        )}
      </div>
    </div>
  );
}

function EditModal({ editing, setEditing, onSave }) {
  const tabelle = Array.isArray(editing.tabelle) ? editing.tabelle : [];
  const toggleTab = (k) => {
    const has = tabelle.includes(k);
    const next = has ? tabelle.filter(x => x !== k) : [...tabelle, k];
    setEditing({ ...editing, tabelle: next });
  };
  const [tipiNomina, setTipiNomina] = useState([]);
  useEffect(() => {
    api.get("/privacy/modelli/tipi-nomina").then(r => {
      setTipiNomina(r.data?.items || []);
    }).catch(() => { });
  }, []);
  return (
    <div onClick={() => setEditing(null)}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} data-testid="pm-edit-modal"
        style={{ background: "white", padding: 20, minWidth: 460, maxWidth: 640, maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: "#0d47a1" }}>
          {editing._isNew ? "Nuovo modello" : `Modifica ${editing.codice}`}
        </div>
        <Field label="Ambito" testid="pm-f-ambito">
          <div style={{ fontSize: 11, color: "#666", marginBottom: 6 }}>
            Pagina in cui il modello apparirà nel dropdown <b>Stampa/Anteprima</b>
            (es. <i>Nomina</i> = visibile in Privacy → Nomine).
          </div>
          <div data-testid="pm-f-ambito-bar"
            style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {AMBITI.map(a => {
              const active = (editing.ambito || "trattamento") === a.k;
              const disabled = !editing._isNew;
              return (
                <button key={a.k} type="button"
                  onClick={() => { if (!disabled) setEditing({ ...editing, ambito: a.k }); }}
                  data-testid={`pm-f-ambito-pill-${a.k}`}
                  disabled={disabled && !active}
                  style={{
                    padding: "5px 12px", fontSize: 11, fontWeight: 700, borderRadius: 16,
                    cursor: disabled ? "default" : "pointer",
                    background: active ? "#0d47a1" : "white",
                    color: active ? "white" : (disabled ? "#bbb" : "#37474f"),
                    border: active ? "1px solid #0d47a1" : "1px solid #cfd8dc",
                    opacity: disabled && !active ? 0.5 : 1,
                  }}>
                  {a.label}
                </button>
              );
            })}
          </div>
          {!editing._isNew && (
            <div style={{ fontSize: 10, color: "#888", marginTop: 4 }}>L&apos;ambito non è modificabile dopo la creazione del modello.</div>
          )}
        </Field>
        <Field label="Fascicoli di inclusione" testid="pm-f-sezione">
          <div style={{ fontSize: 11, color: "#666", marginBottom: 6 }}>
            Indica <b>uno o più fascicoli</b> in cui questo documento dovrà essere incluso.
            Lascia vuoto se il documento è da stampare singolarmente al bisogno (non in nessun fascicolo).
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {[
              { k: "trattamento", label: "📋 Trattamento", color: "#1565c0" },
              { k: "anagrafica", label: "👤 Anagrafica", color: "#6a1b9a" },
              { k: "esterno", label: "🌐 Esterni", color: "#00838f" },
            ].map(s => {
              const sez = Array.isArray(editing.sezioni) ? editing.sezioni : (editing.sezione ? [editing.sezione] : []);
              const active = sez.includes(s.k);
              return (
                <button key={s.k} type="button"
                  onClick={() => {
                    const cur = Array.isArray(editing.sezioni) ? [...editing.sezioni] : (editing.sezione ? [editing.sezione] : []);
                    const idx = cur.indexOf(s.k);
                    if (idx >= 0) cur.splice(idx, 1);
                    else cur.push(s.k);
                    setEditing({ ...editing, sezioni: cur, sezione: cur[0] || "" });
                  }}
                  data-testid={`pm-f-sezione-pill-${s.k}`}
                  style={{
                    padding: "6px 14px", fontSize: 12, fontWeight: 700, borderRadius: 16,
                    cursor: "pointer",
                    background: active ? s.color : "white",
                    color: active ? "white" : "#37474f",
                    border: active ? `2px solid ${s.color}` : "2px solid #cfd8dc",
                    boxShadow: active ? `0 0 0 2px ${s.color}33` : "none",
                  }}>
                  {active && "✓ "}{s.label}
                </button>
              );
            })}
          </div>
        </Field>
        <Field label="Obbligatorietà" testid="pm-f-obblig">
          <div style={{ display: "flex", gap: 16 }}>
            <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
              <input type="checkbox" data-testid="pm-f-obbl"
                checked={!!editing.obbligatorio}
                onChange={e => setEditing({ ...editing, obbligatorio: e.target.checked })} />
              <span>📌 Sempre obbligatorio (deve essere presente nel fascicolo)</span>
            </label>
            <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
              <input type="checkbox" data-testid="pm-f-obbl-nominato"
                checked={!!editing.obbligatorio_se_nominato}
                onChange={e => setEditing({ ...editing, obbligatorio_se_nominato: e.target.checked })} />
              <span>🎯 Obbligatorio solo se nominato</span>
            </label>
          </div>
        </Field>
        {editing.ambito === "nomina" && (
          <Field label="Tipo Nomina collegato" testid="pm-f-tipo-nomina">
            <div style={{ fontSize: 11, color: "#666", marginBottom: 6 }}>
              Solo per modelli di tipo <b>Nomina</b>. Indica <b>per quale tipo di nomina</b> è questo documento.
              Sarà incluso nel <b>Fascicolo Anagrafica del docente</b> SOLO se il docente ha ricevuto questa specifica nomina.
            </div>
            <select data-testid="pm-f-tipo-nomina-sel"
              value={editing.tipo_nomina_codice || ""}
              onChange={e => setEditing({ ...editing, tipo_nomina_codice: e.target.value })}
              style={{ width: "100%", padding: "7px 10px", border: "2px solid #7b1fa2", fontSize: 13, fontWeight: 600, background: "white" }}>
              <option value="">— Non specificato (modello generico, sempre incluso se in fascicolo) —</option>
              {(tipiNomina || []).map(t => (
                <option key={t.codice} value={t.codice}>{t.codice} · {t.nome || t.descrizione}</option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Nome" testid="pm-f-nome">
          <input type="text" value={editing.nome || ""} onChange={e => setEditing({ ...editing, nome: e.target.value })}
            style={{ width: "100%", padding: "6px 10px", border: "1px solid #ccc", fontSize: 12, boxSizing: "border-box" }} />
        </Field>
        <Field label="Descrizione" testid="pm-f-descr">
          <textarea value={editing.descrizione || ""} onChange={e => setEditing({ ...editing, descrizione: e.target.value })}
            rows={2}
            style={{ width: "100%", padding: "6px 10px", border: "1px solid #ccc", fontSize: 12, boxSizing: "border-box" }} />
        </Field>
        <Field label="Tabelle interessate (sorgenti dei placeholder)" testid="pm-f-tabelle">
          <div style={{ fontSize: 11, color: "#666", marginBottom: 6 }}>
            Archivi da cui il modello <b>preleva i placeholder</b> utilizzabili nel <code>.docx</code>.
            La guida e l&apos;analisi placeholder mostreranno solo questi (placeholder fuori da queste tabelle = <span style={{ color: "#e65100" }}>⚠ orfani</span> e non verranno compilati).
            Lascia tutto deselezionato per il comportamento storico (riconoscimento per ambito).
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, padding: "6px 8px", border: "1px solid #cfd8dc", background: "#fafafa", borderRadius: 4 }}>
            {TABELLE_OPTS.map(opt => {
              const checked = tabelle.includes(opt.k);
              return (
                <label key={opt.k}
                  data-testid={`pm-f-tab-${opt.k}`}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 6px", cursor: "pointer", fontSize: 12, background: checked ? "#e3f2fd" : "transparent", borderRadius: 3 }}>
                  <input type="checkbox" checked={checked} onChange={() => toggleTab(opt.k)} />
                  <span>{opt.icon} {opt.label}</span>
                </label>
              );
            })}
          </div>
          {tabelle.length > 0 && (
            <div style={{ marginTop: 6, fontSize: 11, color: "#0d47a1" }}>
              ✓ Selezionate: <b>{tabelle.join(", ")}</b>
            </div>
          )}
        </Field>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12 }}>
          <button onClick={() => setEditing(null)} data-testid="pm-cancel"
            style={{ padding: "8px 16px", background: "white", border: "1px solid #999", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            Annulla
          </button>
          <button onClick={onSave} disabled={!editing.nome} data-testid="pm-save"
            style={{ padding: "8px 16px", background: editing.nome ? "#2e7d32" : "#bbb", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: editing.nome ? "pointer" : "not-allowed" }}>
            Salva
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#555", marginBottom: 3 }} data-testid={testid}>{label}</div>
      {children}
    </div>
  );
}
